import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, withCtx as _withCtx, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import { computed, nextTick, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import CmTable from '@/components/cmTable/index.vue'
import ExportDropdown from '@/views/dashboard/_components/export-dropdown/export-dropdown.vue'
import RawDataDrawer from '@/views/dashboard/_components/raw-data-drawer/raw-data-drawer.vue'
import { FormType, ObjectFormItem, DateType, FormSize } from '@/components/cmForm/types'
import { TableActOpt, TableOpt } from '@/components/cmTable/types'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useParseTime } from '@/hooks'
import { formatTimeStamp } from '@/utils/time'
import { computerInfoCols } from '@/views/dashboard/data-safe/ransomware-protection/types/ransomware-incident'
import {
  SoftwareModifyData,
  SoftwareModifyLogService,
} from './software-modify-log.service'

type SoftwareModifyQueryParams = {
  key_word: string
  start_time?: string
  end_time?: string
  page: number
  limit: number
  dateRange: [string, string]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'software-modify-log',
  props: {
    currentTab: { default: 'software-modify-log' },
    embedDefaults: { default: null }
  },
  setup(__props: any) {

const props = __props

const { t, locale } = useI18n()
const injector = useReflectiveInjector([SoftwareModifyLogService])
const service = injector.get(SoftwareModifyLogService)

const fallbackDateRange: [string, string] = [
  dayjs().subtract(30, 'day').format('YYYY-MM-DD'),
  dayjs().format('YYYY-MM-DD'),
]

const initialDateRange: [string, string] = props.embedDefaults?.dateRange?.length === 2
  ? [...props.embedDefaults.dateRange]
  : fallbackDateRange
const [initialStartTime, initialEndTime] = useParseTime(initialDateRange)

const queryParams = reactive<SoftwareModifyQueryParams>({
  key_word: '',
  start_time: initialStartTime != null ? String(initialStartTime) : undefined,
  end_time: initialEndTime != null ? String(initialEndTime) : undefined,
  page: 1,
  limit: 20,
  dateRange: [...initialDateRange],
})

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)

const shouldAutoLoad = computed(
  () => props.currentTab === 'software-modify-log' || props.embedDefaults?.dateRange?.length === 2,
)

const softwareActionLabel = computed(() => {
  void locale.value
  const labels = [
    '',
    t('noticeManage.softwareModifyUninstall'),
    t('noticeManage.softwareModifyUpgrade'),
    t('noticeManage.softwareModifyInstall'),
  ]
  return (action: number | string) => labels[Number(action)] ?? '--'
})

const filterFormColumns = computed<Partial<ObjectFormItem<any, any>>>(() => {
  void locale.value
  return {
    dateRange: {
      colOpt: { style: 'flex: 0 0 280px' },
      label: t('noticeManage.filterDateRange'),
      type: FormType.Date,
      dateType: DateType.DateRange,
      outSearchFiledFlag: true,
      events: {
        change: (val: [string, string] | undefined) => {
          if (val?.length === 2) {
            const [start, end] = useParseTime(val)
            queryParams.start_time = start != null ? String(start) : undefined
            queryParams.end_time = end != null ? String(end) : undefined
          } else {
            queryParams.start_time = undefined
            queryParams.end_time = undefined
          }
          void refreshList()
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('common.startDate'),
        'end-placeholder': t('common.endDate'),
        clearable: false,
      },
    },
  }
})

const tableActOpt = computed<TableActOpt>(() => {
  void locale.value
  return {
    columnOpt: {
      label: t('noticeManage.operation'),
      width: 100,
      align: 'left',
      fixed: 'right',
    },
    isLink: true,
    actionButtons: () => [
      {
        name: t('noticeManage.rawData'),
        type: 'rawData',
        opt: { size: FormSize.Small, type: 'text', class: 'cm-link-light underline' },
      },
    ],
  }
})

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  const actionLabel = softwareActionLabel.value
  return {
    path: 'list',
    totalPath: 'total',
    height: '100%',
    bodyClass:'el-card',
    bodyStyle: props.embedDefaults ? 'padding: 10px 16px;border-radius: var(--el-border-radius-large);background: #fff' : 'padding: 16px;border-radius: var(--el-border-radius-large);background: #fff',
    showActionBar: false,
    showForm: false,
    showRightToolbar: false,
    selection: false,
    showTip: false,
    columns: [
      {
        label: t('noticeManage.time'),
        prop: 'change_time',
        columnOpt: { minWidth: 160, 'class-name': 'cm-ui-pl20' },
        formatter: (_row: SoftwareModifyData, value: number | string) =>
          value ? formatTimeStamp(String(Number(value)), 'YYYY-MM-DD HH:mm') : '--',
      },
      {
        label: t('noticeManage.eventType'),
        prop: 'action',
        columnOpt: { minWidth: 100 },
        formatter: (_row: SoftwareModifyData, value: number | string) => actionLabel(value),
      },
      ...computerInfoCols(true, true, t,{ip:false,mac:false}),
      {
        label: t('noticeManage.softName'),
        prop: 'soft_name',
        columnOpt: { minWidth: 180 },
      },
    ],
  }
})

const isShowRawData = ref(false)
const currentRow = ref<Record<string, unknown>>({})

function dataApi(params: Record<string, unknown>) {
  const p = params as unknown as SoftwareModifyQueryParams
  return service.getSoftwareModifyList({
    key_word: p.key_word || '',
    start_time: p.start_time as never,
    end_time: p.end_time as never,
    page: Number(params.page),
    limit: Number(params.limit),
  })
}

function handleAction({ type, rowItem }: { type: string; rowItem?: SoftwareModifyData }) {
  if (type === 'rawData' && rowItem) {
    currentRow.value = rowItem as unknown as Record<string, unknown>
    isShowRawData.value = true
  }
}

async function handleSearch(value: string) {
  await nextTick()
  queryParams.key_word = value
  void refreshList()
}

async function handleQuery(form: Partial<SoftwareModifyQueryParams>) {
  if (form.dateRange?.length === 2) {
    const [start, end] = useParseTime(form.dateRange)
    form.start_time = start != null ? String(start) : undefined
    form.end_time = end != null ? String(end) : undefined
  }
  Object.assign(queryParams, form)
  await refreshList()
}

function handleReset() {
  queryParams.key_word = ''
  const resetRange: [string, string] = props.embedDefaults?.dateRange?.length === 2
    ? [...props.embedDefaults.dateRange]
    : fallbackDateRange
  queryParams.dateRange = resetRange
  const [start, end] = useParseTime(resetRange)
  queryParams.start_time = start != null ? String(start) : undefined
  queryParams.end_time = end != null ? String(end) : undefined
  void refreshList()
}

function download(type: 0 | 1) {
  service.download(type, queryParams, t('noticeManage.exportFileSoftwareModifyLog'))
}

watch(
  () => [props.currentTab, props.embedDefaults] as const,
  async ([tab, embed]) => {
    if (tab === 'software-modify-log' || embed?.dateRange?.length === 2) {
      await refreshList()
    }
  },
)

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(CmTable, {
      ref_key: "tableRef",
      ref: tableRef,
      modelValue: queryParams,
      "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
      "first-loading": shouldAutoLoad.value,
      "table-opt": tableOpt.value,
      "table-act-opt": tableActOpt.value,
      api: dataApi,
      "page-option": {
      pageNumName: 'page',
      pageSizeName: 'limit',
    },
      onHandleAction: handleAction
    }, {
      header: _withCtx(() => [
        _createVNode(SearchFilterBar, {
          style: {"padding":"0","background-color":"transparent"},
          modelValue: queryParams,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams) = $event)),
          "form-columns": filterFormColumns.value,
          "show-search": true,
          "sync-search-from-field": "key_word",
          "search-placeholder": _ctx.$t('noticeManage.searchPlaceholderComputerUserSoft'),
          "popover-threshold": 2,
          onSearch: handleSearch,
          onQuery: handleQuery,
          onReset: handleReset
        }, {
          actions: _withCtx(() => [
            _createVNode(ExportDropdown, {
              "button-text-i18n-key": "noticeManage.exportRecord",
              onExport: download
            })
          ]),
          _: 1
        }, 8, ["modelValue", "form-columns", "search-placeholder"])
      ]),
      _: 1
    }, 8, ["modelValue", "first-loading", "table-opt", "table-act-opt"]),
    _createVNode(RawDataDrawer, {
      modelValue: isShowRawData.value,
      "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((isShowRawData).value = $event)),
      "raw-data": currentRow.value
    }, null, 8, ["modelValue", "raw-data"])
  ], 64))
}
}

})